import { Injectable } from "@angular/core";
import dicomParser from "dicom-parser";
import * as zip from "@zip.js/zip.js";
import { sntLog } from "src/app/common/logger";

enum DICOM_PROPERTIES {
  AccessionNumber = "x00080050",
  StationName = "x00081010",
  Modality = "x00080060",
  StudyInstanceUID = "x0020000d",
  SeriesDescription = "x0008103e",
  StudyDescription = "x00081030",
  AcquisitionDate = "x00080022",
  PatientID = "x00100020",
  PatientName = "x00100010",
  PatientSpeciesDescription = "x00102201",
  PatientBreedDescription = "x00102292"
}

export type DicomServiceParseResult = Partial<{
  accessionNumber: string;
  stationName: string;
  modality: string;
  studyInstanceUID: string;
  seriesDescription: string;
  studyDescription: string;
  acquisitionDate: string;
  ownerNominative: string;
  patientId: string;
  patientName: string;
  patientSpeciesDescription: string;
  patientBreedDescription: string;
}>;

const logger = sntLog("DicomService");

@Injectable()
export class DicomService {
  constructor() {
    zip.configure({
      useWebWorkers: true
    });
  }

  async validateZip({ file }: { file: Blob }): Promise<boolean> {
    try {
      const zipEntries: zip.Entry[] = await new zip.ZipReader(
        new zip.BlobReader(file)
      ).getEntries();

      const hasDcmFile = zipEntries.some(
        (entry: zip.Entry) =>
          !entry.directory && entry.filename.endsWith(".dcm")
      );

      return hasDcmFile;
    } catch (ex) {
      logger.error("Error validating ZIP file", ex);
      return false;
    }
  }

  async parse({ file }: { file: Blob }): Promise<DicomServiceParseResult> {
    const zipEntries: zip.Entry[] = await new zip.ZipReader(
      new zip.BlobReader(file)
    ).getEntries();

    const dcmFileEntries = zipEntries.filter((entry: zip.Entry) => {
      return !entry.directory && entry.filename.endsWith(".dcm");
    });

    const firstDcmFileEntry: zip.FileEntry = <zip.FileEntry>dcmFileEntries[0];

    logger.debug(`zip entry: ${firstDcmFileEntry.filename}`);

    const firstDcmData: Uint8Array = await firstDcmFileEntry.getData(
      new zip.Uint8ArrayWriter()
    );

    const dataSet: dicomParser.DataSet = dicomParser.parseDicom(firstDcmData);

    const ownerNominativeAndPatientName = dataSet.string(
      DICOM_PROPERTIES.PatientName
    );
    const ownerNominative = ownerNominativeAndPatientName?.split("^")[0];
    const patientName = ownerNominativeAndPatientName?.split("^")[1];

    const result: DicomServiceParseResult = {
      accessionNumber: dataSet.string(DICOM_PROPERTIES.AccessionNumber),
      stationName: dataSet.string(DICOM_PROPERTIES.StationName),
      modality: dataSet.string(DICOM_PROPERTIES.Modality),
      studyInstanceUID: dataSet.string(DICOM_PROPERTIES.StudyInstanceUID),
      seriesDescription: dataSet.string(DICOM_PROPERTIES.SeriesDescription),
      studyDescription: dataSet.string(DICOM_PROPERTIES.StudyDescription),
      acquisitionDate: dataSet.string(DICOM_PROPERTIES.AcquisitionDate),
      patientId: dataSet.string(DICOM_PROPERTIES.PatientID),
      ownerNominative,
      patientName,
      patientSpeciesDescription: dataSet.string(
        DICOM_PROPERTIES.PatientSpeciesDescription
      ),
      patientBreedDescription: dataSet.string(
        DICOM_PROPERTIES.PatientBreedDescription
      )
    };

    logger.debug(JSON.stringify(result));

    return result;
  }
}
